import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import {
  DateRange,
  MatDateRangePicker,
  MatDatepicker,
  MatDatepickerInputEvent,
} from "@angular/material/datepicker";
import { debounceTime, distinctUntilChanged, skip, Subscription } from "rxjs";
import {
  FilterConfig,
  FiltersEnum,
  FilterLayout,
  DateRangeOptions,
} from "@assoanalytics/app/core/enum/Filters";
import { FiltersOptions } from "@assoanalytics/app/core/models/filters-model";
import { NGXLogger } from "ngx-logger";
import { DateRangeComponent } from "../date-range/date-range.component";
import { MaterialModule } from "@assoanalytics/app/material.module";
import { provideTablerIcons, TablerIconComponent } from "angular-tabler-icons";
import {
  IconAdjustmentsAlt,
  IconAdjustmentsOff,
} from "angular-tabler-icons/icons";
import moment from "moment";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from "@angular/material/core";
import { DateInputMaskDirective } from "../../directives/date-input-mask.directive";
import { ITALIAN_DATE_FORMATS } from "@assoanalytics/app/core/utils/custom-date-adapter";
import { ItalianDateAdapter } from "@assoanalytics/app/core/utils/italian-date-asapter";
import { FilterInputService } from "../../services/filter-input/filter-input.service";
import { AuthService } from "@assoanalytics/app/core/services/auth.service";
import { Plant } from "@assoanalytics/app/core/models/plant";
import { Product } from "@assoanalytics/app/core/models/product";
import { Service } from "@assoanalytics/app/core/models/service";
import { PaymentType } from "@assoanalytics/app/core/models/payment-type";
import { DateService } from "../../services/date.service";
import { SharedModule } from "../../shared.module";
import { FilterService } from "../../services/filter/filter.service";
import { PaymentMethod } from "@assoanalytics/app/core/models/archives.model";
import {
  WlfaSupplier,
  WlfaProduct,
  WlfaPlant,
  WlfaTerminal,
} from "@assoanalytics/app/core/models/wlfa.model";
import {
  AssoPlant,
  AssoCampaign,
  AssoUser,
} from "@assoanalytics/app/core/models/asso-model";

/**
 * @description component to show a toolbar with series of input of filters
 * @param {string[]} filtersList
 * @class FilterBarComponent
 */
@Component({
  selector: "app-filter-bar",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    SharedModule,
    MaterialModule,
    TablerIconComponent,
    DateInputMaskDirective,
  ],

  providers: [
    { provide: MAT_DATE_LOCALE, useValue: "it-IT" },
    { provide: MAT_DATE_FORMATS, useValue: ITALIAN_DATE_FORMATS },
    {
      provide: DateAdapter,
      useClass: ItalianDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    provideTablerIcons({
      IconAdjustmentsAlt,
      IconAdjustmentsOff,
    }),
  ],
  templateUrl: "./filter-bar.component.html",
  styleUrls: ["./filter-bar.component.scss"],
})
export class FilterBarComponent implements OnChanges, OnDestroy {
  constructor(
    private filterInputService: FilterInputService,
    private logger: NGXLogger,
    private fb: FormBuilder,
    private authService: AuthService,
    private dateService: DateService,
  ) {
    FilterInputService.showPageFilters.subscribe((show: boolean) => {
      this.showFilters = show;
    });
  }

  @Input({ required: true }) filtersList: FilterLayout = [];
  @Input() filtersOptions: FiltersOptions = { withAll: true };
  @Input() fromBreadcrumb: boolean = false;
  @Input() showButton: boolean = false;
  @Input() filtersReset: boolean = false;
  @Output() onFilterEvent: EventEmitter<FormGroup> = new EventEmitter();

  showFilters: boolean = false;
  formReady: boolean = false;
  today = new Date();

  filtersType = FiltersEnum;

  /** Opzioni date range dell'ultimo filtro date_range configurato */
  dateRangeOptions: DateRangeOptions | undefined;
  /** Max dinamico per la data fine: aggiornato al cambio della data inizio quando maxSpan è attivo */
  dateRangeMaxEndDate: Date | null = null;

  formFilter: FormGroup = new FormGroup({});

  // nasconde l'option "Tutti" di asso_user
  showUserAllOption = true

  // Dati delle liste filtrate
  filterData = {
    campaigns: [] as { id: number; name: string; type: string }[],
    plants: [] as Plant[],
    products: [] as Product[],
    services: [] as Service[],
    paymentTypes: [] as PaymentType[],
    paymentMethods: [] as PaymentMethod[],
    suppliers: [] as WlfaSupplier[],
    wlfaProducts: [] as WlfaProduct[],
    wlfaTerminals: [] as WlfaTerminal[],
    wlfaPlants: [] as WlfaPlant[],
    assoPlants: [] as { id: string; name: string }[],
    assoOnlyPlants: [] as { id: number; name: string }[],
    assoCampaigns: [] as AssoCampaign[],
    assoUsers: [] as AssoUser[],
  };

  // FormControl per le ricerche
  searchControls = {
    campaigns: new FormControl(),
    plant: new FormControl(),
    product: new FormControl(),
    service: new FormControl(),
    paymentMethod: new FormControl(),
    supplier: new FormControl(),
    wlfaProduct: new FormControl(),
    wlfaTerminal: new FormControl(),
    wlfaPlant: new FormControl(),
    assoPlant: new FormControl(),
    assoOnlyPlant: new FormControl(),
    assoCampaign: new FormControl(),
    assoUser: new FormControl(),
  };

  dateRangeGroupFilterCtrl: FormGroup<{
    startDate: FormControl<string | null>;
    endDate: FormControl<string | null>;
  }>;

  private ngxSearchSubsArray: Subscription[] = [];

  private customFilterValuesSub: Subscription;

  private wlfaSupplierSub: Subscription;

  // Stato UI per la select utenti Asso
  assoUsersSearchText = "";
  assoUsersLoading = false;
  assoUsersHasMinChars = true;
  private _assoSelectOpen = false;

  pageCustomFilters: {
    page?: string;
    values: any;
  };

  // Getter per il template
  get campaignsParsedList() {
    return this.filterData.campaigns;
  }
  get plantsParsedList() {
    return this.filterData.plants;
  }
  get productsParsedList() {
    return this.filterData.products;
  }
  get servicesParsedList() {
    return this.filterData.services;
  }
  get paymentTypesParsedList() {
    return this.filterData.paymentTypes;
  }
  get suppliersParsedList() {
    return this.filterData.suppliers;
  }
  get wlfaProductsParsedList() {
    return this.filterData.wlfaProducts;
  }
  get wlfaPlantsParsedList() {
    return this.filterData.wlfaPlants;
  }
  get assoPlantsParsedList() {
    return this.filterData.assoPlants;
  }
  get assoOnlyPlantsParsedList() {
    return this.filterData.assoOnlyPlants;
  }
  get assoCampaignsParsedList() {
    return this.filterData.assoCampaigns;
  }
  get assoUsersParsedList() {
    return this.filterData.assoUsers;
  }

  // Getter per i FormControl di ricerca
  get ngxCampaignSearchCtrl() {
    return this.searchControls.campaigns;
  }
  get ngxPlantSearchCtrl() {
    return this.searchControls.plant;
  }
  get ngxProductSearchCtrl() {
    return this.searchControls.product;
  }
  get ngxServiceSearchCtrl() {
    return this.searchControls.service;
  }
  get ngxPaymentMethodSearchCtrl() {
    return this.searchControls.paymentMethod;
  }
  get ngxSupplierSearchCtrl() {
    return this.searchControls.supplier;
  }
  get ngxWlfaProductSearchCtrl() {
    return this.searchControls.wlfaProduct;
  }
  get ngxWlfaTerminalSearchCtrl(){
    return this.searchControls.wlfaTerminal
  }
  get ngxWlfaPlantSearchCtrl() {
    return this.searchControls.wlfaPlant;
  }
  get ngxAssoOnlyPlantSearchCtrl() {
    return this.searchControls.assoOnlyPlant;
  }
  get ngxAssoCampaignSearchCtrl() {
    return this.searchControls.assoCampaign;
  }
  get ngxAssoUserSearchCtrl() {
    return this.searchControls.assoUser;
  }
  get ngxAssoPlantSearchCtrl(){
    return this.searchControls.assoPlant
  }

  ngOnChanges(changes: SimpleChanges): void {
    // L'istanza "solo bottone" non deve inizializzare form e sorgenti dati:
    // evita doppie chiamate API quando la pagina renderizza due FilterBar.
    if (this.showButton) {
      return;
    }   

    if (changes["filtersList"] || this.filtersReset) {
      this.formReady = false;
      // Reset filtri
      Object.keys(this.formFilter.controls).forEach((control) =>
        this.formFilter.removeControl(control),
      );

      // Ottiene la lista piatta di filtri indipendentemente dal formato
      const filtersToProcess = this.getFiltersFromLayout();

      // Verifica se ci sono filtri che necessitano dei meta da transactions
      const needsTransactionsMeta = filtersToProcess.some((filter) =>
        [
          FiltersEnum.plant,
          FiltersEnum.multi_plant,
          FiltersEnum.product,
          FiltersEnum.multi_product,
          FiltersEnum.service,
          FiltersEnum.payment_method,
        ].includes(filter.filterName),
      );

      // Se necessario, carica i meta prima di processare i filtri
      if (needsTransactionsMeta) {
        this.filterInputService.getAnalyticsMeta().subscribe(() => {
          this.processFilters(filtersToProcess);
        });
      } else {
        this.processFilters(filtersToProcess);
      }

      this.filtersReset = false;
    }
  }

  private processFilters(filtersToProcess: FilterConfig[]): void {
    // Forza la fetch dei prodotti se richiesto dai filtri
    const needsProducts = filtersToProcess.some((filter: FilterConfig) =>
      [FiltersEnum.product, FiltersEnum.multi_product].includes(
        filter.filterName,
      ),
    );
    if (needsProducts) {
      this.filterInputService.getProductsList();
    }

    filtersToProcess.forEach((filter) => {
      let subscription: Subscription;

      switch (filter.filterName) {
        // Date range
        case FiltersEnum.date_range:
          this.dateRangeOptions = filter.dateRangeOptions;
          const defaultDateRangeValues =
            this.resolveDateRangeDefaultValues(filter);

          // Calcola il max assoluto per la data fine
          const absoluteMax =
            filter.dateRangeOptions?.maxDate ??
            (this.filtersOptions.daterangeWithoutLimits ? null : this.today);

          // if (filter.dateRangeOptions?.maxSpan) {
          //   const baseStartDate =
          //     defaultDateRangeValues.startDate ?? this.today.toISOString();
          //   const spanMax = moment(baseStartDate)
          //     .add(
          //       filter.dateRangeOptions.maxSpan.value,
          //       filter.dateRangeOptions.maxSpan
          //         .unit as moment.unitOfTime.DurationConstructor,
          //     )
          //     .toDate();
          //   this.dateRangeMaxEndDate =
          //     absoluteMax && spanMax > absoluteMax ? absoluteMax : spanMax;
          // } else {
            this.dateRangeMaxEndDate = absoluteMax;
          // }

          this.dateRangeGroupFilterCtrl = new FormGroup({
            startDate: new FormControl(
              defaultDateRangeValues.startDate,
              filter.validators ? [...filter.validators, Validators.required] : [Validators.required],
            ),
            endDate: new FormControl(
              defaultDateRangeValues.endDate,
              filter.validators ? [...filter.validators, Validators.required] : [Validators.required],
            ),
          });

          if (this.dateRangeOptions?.maxSpan) {
            this.dateRangeGroupFilterCtrl.addValidators(this.dateRangeMaxSpanValidatorFn());
          }

          this.formFilter.addControl(
            "date_range",
            this.dateRangeGroupFilterCtrl,
          );
          break;

        // CAMPAIGNS
        case FiltersEnum.campaigns:
          this.formFilter.addControl(
            "campaigns",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          // Usa sempre la subscription per aggiornare la lista
          this.filterInputService
            .getCampaignsList()
            .subscribe((campaigns: any) => {
              this.filterData.campaigns = campaigns.data;
            });

          this.searchControls.campaigns = new FormControl("");

          break;

        // ASSO CAMPAIGNS
        case FiltersEnum.asso_campaign:
          this.formFilter.addControl(
            "asso_campaign",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService.getAssoCampaignsList().subscribe((campaigns) => {
            this.filterData.assoCampaigns = campaigns;
          });

          this.searchControls.assoCampaign = new FormControl();
          subscription = this.searchControls.assoCampaign.valueChanges.subscribe(
            (value) => {
              this.filterData.assoCampaigns =
                this.filterInputService.filterAssoCampaignsList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // PRODUCT
        case FiltersEnum.product:
          this.formFilter.addControl(
            "product",
            new FormControl(null, filter.validators ? filter.validators : []),
          );
          this.searchControls.product = new FormControl();
          this.filterInputService.getProductsList().subscribe((products) => {
            this.filterData.products = products;
            subscription = this.searchControls.product.valueChanges.subscribe(
              (value) => {
                if (value && value.length >= 2) {
                  this.filterData.products =
                    this.filterInputService.filterProductsList(value);
                } else {
                  this.filterData.products = products;
                }
              },
            );
            this.ngxSearchSubsArray.push(subscription);
          });

          break;

        // MULTI PRODUCT
        case FiltersEnum.multi_product:
          this.formFilter.addControl(
            "products",
            new FormControl(null, filter.validators ? filter.validators : []),
          );
          this.searchControls.product = new FormControl();
          this.filterInputService.getProductsList().subscribe((products) => {
            this.filterData.products = products;
            subscription = this.searchControls.product.valueChanges.subscribe(
              (value) => {
                if (value && value.length >= 2) {
                  this.filterData.products =
                    this.filterInputService.filterProductsList(value);
                } else {
                  this.filterData.products = products;
                }
              },
            );
            this.ngxSearchSubsArray.push(subscription);
          });

          break;

        // PLANT
        case FiltersEnum.plant:
          this.formFilter.addControl(
            "plant",
            new FormControl(
              { value: "", disabled: false },
              filter.validators ? filter.validators : [],
            ),
          );
          this.filterData.plants = this.filterInputService.plantsList;

          this.searchControls.plant = new FormControl();
          subscription = this.searchControls.plant.valueChanges.subscribe(
            (value) => {
              this.filterData.plants =
                this.filterInputService.filterPlantList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // MULTI PLANTS
        case FiltersEnum.multi_plant:
          this.formFilter.addControl(
            "plant",
            new FormControl([], filter.validators ? filter.validators : []),
          );

          this.filterData.plants = this.filterInputService.plantsList;

          this.searchControls.plant = new FormControl();
          subscription = this.searchControls.plant.valueChanges.subscribe(
            (value) => {
              this.filterData.plants =
                this.filterInputService.filterPlantList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // ASSO PLANT
        case FiltersEnum.asso_plant:
          this.formFilter.addControl(
            "asso_plant",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService.getAssoPlantsList().subscribe((plants) => {
            this.filterData.assoPlants = plants;
          });

          this.searchControls.assoPlant = new FormControl();
          subscription = this.searchControls.assoPlant.valueChanges.subscribe(
            (value) => {
              this.filterData.assoPlants =
                this.filterInputService.filterAssoPlantsList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // ASSO MULTI PLANTS
        case FiltersEnum.asso_multi_plant:
          this.formFilter.addControl(
            "asso_plant",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService.getAssoPlantsList().subscribe((plants) => {
            this.filterData.assoPlants = plants;
          });

          this.searchControls.assoPlant = new FormControl();
          subscription = this.searchControls.assoPlant.valueChanges.subscribe(
            (value) => {
              this.filterData.assoPlants =
                this.filterInputService.filterAssoPlantsList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // ASSO MULTI PLANTS
        case FiltersEnum.asso_only_multi_plant:
          this.formFilter.addControl(
            "asso_only_plant",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService
            .getOnlyAssoPlantsList()
            .subscribe((plants) => {
              this.filterData.assoOnlyPlants = plants;
            });

          this.searchControls.assoOnlyPlant = new FormControl();
          subscription =
            this.searchControls.assoOnlyPlant.valueChanges.subscribe(
              (value) => {
                this.filterData.assoOnlyPlants =
                  this.filterInputService.filterAssoOnlyPlantsList(value);
              },
            );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // SERVICE
        case FiltersEnum.service:
          this.formFilter.addControl(
            "service",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          // Carica la lista servizi in modo asincrono
          this.filterInputService
            .loadServicesListForToday()
            .subscribe((services) => {
              this.filterData.services = services;
            });

          this.searchControls.service = new FormControl();
          subscription = this.searchControls.service.valueChanges.subscribe(
            (value) =>
              (this.filterData.services =
                this.filterInputService.filterServicesList(value)),
          );

          this.ngxSearchSubsArray.push(subscription);

          break;

        // PAYMENT METHOD
        case FiltersEnum.payment_method:
          this.formFilter.addControl(
            "payment_method",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService
            .getPaymentMethods()
            .subscribe((data) => (this.filterData.paymentMethods = data));

          break;

        // SUPPLIER (WLFA)
        case FiltersEnum.wlfa_supplier:
          this.formFilter.addControl(
            "supplier",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService.getSuppliersList().subscribe((suppliers) => {
            this.filterData.suppliers = suppliers;
          });

          this.searchControls.supplier = new FormControl();
          subscription = this.searchControls.supplier.valueChanges.subscribe(
            (value) => {
              this.filterData.suppliers =
                this.filterInputService.filterSuppliersList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // WLFA PRODUCT
        case FiltersEnum.wlfa_product:
          this.formFilter.addControl(
            "wlfa_product",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService
            .getWlfaProductsList()
            .subscribe((products) => {
              this.filterData.wlfaProducts = products;
            });

          this.searchControls.wlfaProduct = new FormControl();
          subscription = this.searchControls.wlfaProduct.valueChanges.subscribe(
            (value) => {
              this.filterData.wlfaProducts =
                this.filterInputService.filterWlfaProductsList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // WLFA PLANT
        case FiltersEnum.wlfa_plant:
          this.formFilter.addControl(
            "wlfa_plant",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService.getWlfaPlantsList().subscribe((plants) => {
            this.filterData.wlfaPlants = plants;
          });

          this.searchControls.wlfaPlant = new FormControl();
          subscription = this.searchControls.wlfaPlant.valueChanges.subscribe(
            (value) => {
              this.filterData.wlfaPlants =
                this.filterInputService.filterWlfaPlantsList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // WLFA TERMINAL
        case FiltersEnum.wlfa_terminal:
          this.formFilter.addControl(
            "wlfa_terminal",
            new FormControl(null, filter.validators ? filter.validators : []),
          );

          this.filterInputService
            .getWlfaTerminalsList()
            .subscribe((terminals) => {
              this.filterData.wlfaTerminals = terminals;
            });

          this.searchControls.wlfaTerminal = new FormControl();
          subscription = this.searchControls.wlfaTerminal.valueChanges.subscribe(
            (value) => {
              this.filterData.wlfaTerminals =
                this.filterInputService.filterWlfaTerminalsList(value);
            },
          );
          this.ngxSearchSubsArray.push(subscription);

          break;

        // ASSO USER
        case FiltersEnum.asso_user:
          this.formFilter.addControl(
            "asso_user",
            new FormControl(null, filter.validators ? filter.validators : []),
          );


          if(filter.validators?.includes(Validators.required)){
            this.showUserAllOption = false
          }

          // Carica la prima pagina senza ricerca
          this.filterInputService.initAssoUsersList();

          // Sincronizza la lista dal BehaviorSubject del service
          this.ngxSearchSubsArray.push(
            this.filterInputService.assoUsers$.subscribe((users) => {
              this.filterData.assoUsers = users;
            }),
            this.filterInputService.assoUsersLoading$.subscribe((loading) => {
              this.assoUsersLoading = loading;
            }),
          );

          this.searchControls.assoUser = new FormControl("");

          // Debounce sull'input di ricerca: min 3 char, attende fine digitazione
          subscription = this.searchControls.assoUser.valueChanges
            .pipe(debounceTime(400), distinctUntilChanged())
            .subscribe((value: string) => {
              const trimmed = (value ?? "").trim();
              if (trimmed.length === 0) {
                // Blocca solo se la select è chiusa (reset post-selezione alla chiusura)
                // Se la select è aperta significa che l'utente ha usato la X: procedi
                if (
                  !this._assoSelectOpen &&
                  this.formFilter.get("asso_user")?.value != null
                )
                  return;
                this.assoUsersHasMinChars = true;
                this.filterInputService.initAssoUsersList();
              } else if (trimmed.length < 3) {
                // Meno di 3 caratteri: mostra hint, non fare chiamate
                this.assoUsersHasMinChars = false;
              } else {
                // Ricerca server-side
                this.assoUsersHasMinChars = true;
                this.filterInputService.initAssoUsersList(trimmed);
              }
            });
          this.ngxSearchSubsArray.push(subscription);

          break;

        // PLATE
        case FiltersEnum.plate:
          this.formFilter.addControl(
            "plate",
            new FormControl(null, filter.validators ? filter.validators : []),
          );
          break;
      }
    });

    // Gestione filtri WLFA dipendenti dal supplier
    const wlfaDependentTypes = [
      FiltersEnum.wlfa_product,
      FiltersEnum.wlfa_plant,
      FiltersEnum.wlfa_terminal,
    ];
    const presentWlfaDependentFilters = filtersToProcess.filter((f) =>
      wlfaDependentTypes.includes(f.filterName),
    );

    if (presentWlfaDependentFilters.length > 0) {
      // Stato iniziale: disabilita se nessun supplier selezionato
      const currentValue = this.filterInputService.selectedSupplier.value;
      const currentSupplierId =
        typeof currentValue === "number"
          ? currentValue
          : (currentValue as any)?.id;
      if (!currentSupplierId) {
        this.setWlfaDependentFiltersState(presentWlfaDependentFilters, false);
      }

      this.wlfaSupplierSub?.unsubscribe();
      this.wlfaSupplierSub = this.filterInputService.selectedSupplier
        .pipe(skip(1))
        .subscribe((supplier) => {
          const supplierId =
            typeof supplier === "number" ? supplier : (supplier as any)?.id;
          if (supplierId) {
            this.setWlfaDependentFiltersState(
              presentWlfaDependentFilters,
              true,
            );
            this.reloadWlfaDependentFilters(presentWlfaDependentFilters);
          } else {
            this.setWlfaDependentFiltersState(
              presentWlfaDependentFilters,
              false,
            );
          }
        });
    }

    // Sposta qui la subscription, dopo aver creato i controlli
    if (this.customFilterValuesSub) {
      this.customFilterValuesSub.unsubscribe();
    }
    this.customFilterValuesSub = this.filterInputService.filterValues.subscribe(
      (newFilters) => {
        this.setFiltersValues(newFilters.values);

        this.pageCustomFilters = {
          page: newFilters.page,
          values: newFilters.values,
        };

        this.logger.debug(
          "Filter Bar - Received custom filters",
          this.pageCustomFilters,
        );
      },
    );

    this.formReady = true;
  }

  showHideFilters() {
    FilterInputService.showPageFilters.next(!this.showFilters);
  }

  onDateInputClick(
    event: MouseEvent,
    picker: MatDatepicker<any> | MatDateRangePicker<any>,
    input: FormControl | FormGroup,
  ) {
    picker.open();
  }

  clearDateInput(input: FormControl | FormGroup) {
    input.reset();
  }

  formatDateOnInput(
    event: MatDatepickerInputEvent<Date, Date | null>,
    input: HTMLInputElement,
  ) {
    let value = input.value.replace(/\D/g, ""); // Remove non-digits

    if (value.length >= 2) {
      value = value.substring(0, 2) + "/" + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + "/" + value.substring(5, 9);
    }

    input.value = value;
  }

  onInputStartDateRange(event: MatDatepickerInputEvent<Date, DateRange<any>>) {
    // check if endDate is after startDate then set to same value
    if (this.dateRangeGroupFilterCtrl.controls.endDate.value && event.value) {
      if (
        moment(this.dateRangeGroupFilterCtrl.controls.endDate.value).valueOf() <
        event.value.valueOf()
      ) {
        this.dateRangeGroupFilterCtrl.controls.endDate.setValue(
          event.value.toISOString(),
        );
      }
    }

    // Aggiorna il max dinamico della data fine quando è configurato maxSpan
    // if (this.dateRangeOptions?.maxSpan) {
    //   const absoluteMax =
    //     this.dateRangeOptions.maxDate ??
    //     (this.filtersOptions.daterangeWithoutLimits ? null : this.today);

    //   if (event.value) {
    //     const spanMax = moment(event.value)
    //       .add(
    //         this.dateRangeOptions.maxSpan.value,
    //         this.dateRangeOptions.maxSpan
    //           .unit as moment.unitOfTime.DurationConstructor,
    //       )
    //       .toDate();
    //     this.dateRangeMaxEndDate =
    //       absoluteMax && spanMax > absoluteMax ? absoluteMax : spanMax;
    //   } else {
    //     // startDate cancellata: torna al max assoluto
    //     this.dateRangeMaxEndDate = absoluteMax;
    //   }

    //   // Se la data fine supera il nuovo max, la regoliamo a from + maxSpan
    //   // invece di azzerarla, così l'utente non deve reimpostarla manualmente
    //   const endVal = this.dateRangeGroupFilterCtrl.controls.endDate.value;
    //   if (
    //     endVal &&
    //     this.dateRangeMaxEndDate &&
    //     moment(endVal).isAfter(this.dateRangeMaxEndDate)
    //   ) {
    //     this.dateRangeGroupFilterCtrl.controls.endDate.setValue(
    //       this.dateRangeMaxEndDate.toISOString(),
    //     );
    //   }
    // }
  }

  /** Restituisce l'etichetta italiana del maxSpan configurato per il date range */
  getMaxSpanLabel(): string {
    if (!this.dateRangeOptions?.maxSpan) return "";
    const { value, unit } = this.dateRangeOptions.maxSpan;
    const unitMap: Record<string, string> = {
      days: value === 1 ? "giorno" : "giorni",
      weeks: value === 1 ? "settimana" : "settimane",
      months: value === 1 ? "mese" : "mesi",
      years: value === 1 ? "anno" : "anni",
    };
    return `${value} ${unitMap[unit] ?? unit}`;
  }

  getFormGroup(controlName: string): FormGroup {
    return this.formFilter.get(controlName) as FormGroup;
  }

  getColSize(size: number) {
    return `col-md-${size} filter-container`;
  }

  isLayoutFormat(): boolean {
    return (
      Array.isArray(this.filtersList) &&
      this.filtersList.length > 0 &&
      "filters" in this.filtersList[0]
    );
  }

  getFiltersFromLayout(): FilterConfig[] {
    // Estrae tutti i filtri dal layout e li appiattisce in un array
    return (this.filtersList as FilterLayout).reduce(
      (acc, row) => [...acc, ...row.filters],
      [] as FilterConfig[],
    );
  }

  setFiltersValues(filters: any) {
    if (!filters) return;
    Object.keys(filters).forEach((key) => {
      const control = this.formFilter.get(key);
      const value = filters[key];

      // Se il controllo è un FormGroup e il valore è un oggetto con startDate/endDate
      if (
        control instanceof FormGroup &&
        value &&
        (value.startDate !== undefined || value.endDate !== undefined)
      ) {
        control.patchValue(value);
      } else {
        // Patch normale per gli altri controlli
        control?.patchValue(value);
      }
    });
  }

  getAssoUserDescription(user: AssoUser) {
    if (user.legalName && user.legalName.trim() !== "") {
      return user.legalName;
    } else if (
      user.surname &&
      user.surname.trim() !== "" &&
      user.name &&
      user.name.trim() !== ""
    ) {
      return `${user.surname} ${user.name}`;
    } else {
      return `${user.username}`;
    }
  }

  onAssoUsersScrollEnd(): void {
    this.filterInputService.loadMoreAssoUsers();
  }

  onAssoSelectOpenedChange(opened: boolean): void {
    this._assoSelectOpen = opened;
    if (!opened) return;
    setTimeout(() => {
      const panel = document.querySelector<HTMLElement>(
        ".cdk-overlay-container .mat-mdc-select-panel",
      );
      if (!panel) return;
      panel.addEventListener(
        "scroll",
        () => {
          if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 40) {
            this.filterInputService.loadMoreAssoUsers();
          }
        },
        { passive: true },
      );
    }, 0);
  }

  wlfaSupplierSelected(supplier: WlfaSupplier) {
    this.filterInputService.selectedSupplier.next(supplier);
  }

  onSubmit() {
    this.logger.debug("Filter Bar Submitted filters", this.formFilter.value);

    if (this.formFilter.invalid) {
      return;
    }

    const rawEntries = Object.entries(this.formFilter.value);

    const filteredEntries = rawEntries.filter(
      ([key, value]) =>
        value !== null &&
        value !== "" &&
        value !== undefined &&
        value !== "all",
    );

    this.logger.debug(
      "Filter Bar Filtered Data",
      Object.fromEntries(filteredEntries),
    );

    // Invia i dati filtrati
    FilterService.updateFilters(Object.fromEntries(filteredEntries));
  }

  resetFilters() {
    this.formFilter.reset();

    let initFilters: any = {};

    // Lista dei filtri date_range da resettare
    const dateRangeFilters: FiltersEnum[] = [FiltersEnum.date_range];

    // Ottiene la lista piatta di filtri indipendentemente dal formato
    const filtersToProcess = this.getFiltersFromLayout();

    // reset dei filtri di tipo date_range
    for (const filterKey of dateRangeFilters) {
      filtersToProcess.forEach((filter) => {
        if (filter.filterName === filterKey) {
          const controlName = FiltersEnum[filter.filterName]; // converto il valore enum in stringa
          const defaultDateRangeValues =
            this.resolveDateRangeDefaultValues(filter);
          const group = this.formFilter.get(controlName) as FormGroup;

          if (group) {
            group.patchValue({
              startDate: defaultDateRangeValues.startDate,
              endDate: defaultDateRangeValues.endDate,
            });

            // // Ricalcola il max dinamico dopo il reset
            // if (this.dateRangeOptions?.maxSpan) {
            //   const absoluteMax =
            //     this.dateRangeOptions.maxDate ??
            //     (this.filtersOptions.daterangeWithoutLimits
            //       ? null
            //       : this.today);
            //   const baseStartDate =
            //     defaultDateRangeValues.startDate ?? this.today.toISOString();
            //   const spanMax = moment(baseStartDate)
            //     .add(
            //       this.dateRangeOptions.maxSpan.value,
            //       this.dateRangeOptions.maxSpan
            //         .unit as moment.unitOfTime.DurationConstructor,
            //     )
            //     .toDate();
            //   this.dateRangeMaxEndDate =
            //     absoluteMax && spanMax > absoluteMax ? absoluteMax : spanMax;
            // }

            initFilters[controlName] = group;
          }
        }
      });
    }

    // reset filtri wlfa (terminale, prodotto, impianto)
    this.setWlfaDependentFiltersState(filtersToProcess, false)

    if (this.pageCustomFilters && this.pageCustomFilters.values) {
      Object.keys(this.pageCustomFilters.values).forEach((filter) => {
        initFilters[filter] = this.pageCustomFilters.values[filter];
      });
    }

    this.formFilter.patchValue(initFilters);

    // Applica immediatamente i filtri resettati con una singola emissione.
    this.onSubmit();
  }

  private resolveDateRangeDefaultValues(filter: FilterConfig): {
    startDate: string | null;
    endDate: string | null;
  } {
    const initialRange = this.dateService.getDateInterval();
    const defaultValues = filter.defaultValues ?? {};

    const startCandidate =
      defaultValues["startDate"] ??
      (!filter.dateRangeOptions?.minDate
        ? initialRange.startDate
        : filter.dateRangeOptions.minDate);

    const endCandidate = defaultValues["endDate"] ?? initialRange.endDate;

    return {
      startDate: this.toIsoString(startCandidate),
      endDate: this.toIsoString(endCandidate),
    };
  }

  private toIsoString(value: unknown): string | null {
    if (!value) return null;
    const formatted = moment(value);
    return formatted.isValid() ? formatted.toISOString() : null;
  }

  private setWlfaDependentFiltersState(
    filtersToProcess: FilterConfig[],
    enabled: boolean,
  ): void {
    const controlMap: Partial<Record<FiltersEnum, string>> = {
      [FiltersEnum.wlfa_product]: "wlfa_product",
      [FiltersEnum.wlfa_plant]: "wlfa_plant",
      [FiltersEnum.wlfa_terminal]: "wlfa_terminal",
    };

    for (const filter of filtersToProcess) {
      const controlName = controlMap[filter.filterName];
      if (controlName) {
        const control = this.formFilter.get(controlName);
        if (control) {
          if (enabled) {
            control.enable();
          } else {
            control.reset();
            control.disable();
          }
        }
      }
    }

    if (!enabled) {
      this.filterData.wlfaProducts = [];
      this.filterData.wlfaPlants = [];
      this.filterData.wlfaTerminals = [];
    }
  }

  private reloadWlfaDependentFilters(filtersToProcess: FilterConfig[]): void {
    for (const filter of filtersToProcess) {
      switch (filter.filterName) {
        case FiltersEnum.wlfa_product:
          this.filterInputService
            .getWlfaProductsList()
            .subscribe((products) => {
              this.filterData.wlfaProducts = products;
            });
          break;
        case FiltersEnum.wlfa_plant:
          this.filterInputService.getWlfaPlantsList().subscribe((plants) => {
            this.filterData.wlfaPlants = plants;
          });
          break;
        case FiltersEnum.wlfa_terminal:
          this.filterInputService
            .getWlfaTerminalsList()
            .subscribe((terminals) => {
              this.filterData.wlfaTerminals = terminals;
            });
          break;
      }
    }
  }


  ngOnDestroy(): void {
    this.ngxSearchSubsArray.forEach((sub) => {
      sub.unsubscribe();
    });
    this.customFilterValuesSub?.unsubscribe();
    this.wlfaSupplierSub?.unsubscribe();
  }

  /**
   * @description funzione da assegnare ad un FormGroup. Prende ogni singolo FormControl al suo interno e imposta un errore se almeno uno di questi ultimi non ha valore. In caso di almeno un valore presente, cancella l'errore su tutti i FormControl ma mantiene eventuali altri errori
   * @private
   * @param {AbstractControl} group
   * @return {*}  {(ValidationErrors | null)}
   * @memberof FilterBarComponent
   */
  private atLeastOneValidatorFn(
    group: AbstractControl,
  ): ValidationErrors | null {
    // Funzione per gestire errori sul singolo control
    const updateControlError = (
      control: AbstractControl,
      shouldSet: boolean,
    ) => {
      // copio gli errori del control
      const currentErrors = { ...(control.errors || {}) };

      if (shouldSet) {
        currentErrors["atLeastOne"] = true;
      } else {
        delete currentErrors["atLeastOne"];
      }

      control.setErrors(
        Object.keys(currentErrors).length ? currentErrors : null,
      );
    };

    const controlsKeys = Object.keys(group.value);
    const values = Object.values(group.value);

    // controllo che ci sia almeno un valore
    const valuesValid = values.filter((value) => !!value).length > 0;

    controlsKeys.forEach((controlKey) => {
      const control = group.get(controlKey)!;

      if (!valuesValid) {
        updateControlError(control, true);
        return { atLeastOne: true }; // opzionale, il formGroup può avere l'errore
      } else {
        updateControlError(control, false);
        return null;
      }
    });

    // opzionale, il formGroup può avere l'errore
    if (!valuesValid) {
      return { atLeastOne: true };
    } else {
      return null;
    }
  }

  /**
   * @description funzione validatrice per il formGroup dateRange da assegnare in caso di definizione maxSpan per validare entrambi gli input. In caso di valore che supera il periodo definito da maxSpan ritorna un errore nel formGroup
   * @private
   * @memberof FilterBarComponent
   */
  private dateRangeMaxSpanValidatorFn(): ValidatorFn {

    function setErrorAndUpdateCtrl(ctrl: AbstractControl, setExceededError: boolean) {

      const ctrlErrors = ctrl.errors || {};

      if (setExceededError) {
        ctrl.setErrors({ ...ctrlErrors, 'invalidSpanRange': true });
      } else {

        delete ctrlErrors['invalidSpanRange'];

        if (Object.keys(ctrlErrors || {}).length) {
          ctrl.setErrors({ ...ctrlErrors });
        } else {
          ctrl.setErrors(null);
        }

      }
      // ctrl.updateValueAndValidity({ emitEvent: false });
    }

    const maxSpan = this.dateRangeOptions?.maxSpan;

    return (group: AbstractControl): ValidationErrors | null => {
      if (!maxSpan) return null;
      if (!group.value || !group.value.startDate || !group.value.endDate) return null;

      const startDateCtrl = group.get('startDate')!;
      const endDateCtrl = group.get('endDate')!;

      const start = moment(startDateCtrl?.value);
      const end = moment(endDateCtrl?.value);

      if (!start.isValid() || !end.isValid()) return null;

      if
        (end.diff(start, maxSpan.unit, true) > maxSpan.value) {

        setErrorAndUpdateCtrl(startDateCtrl, true);
        setErrorAndUpdateCtrl(endDateCtrl, true);
        return {
          maxSpanExceeded: true
        };
      }

      setErrorAndUpdateCtrl(startDateCtrl, false);
      setErrorAndUpdateCtrl(endDateCtrl, false);
      return null;
    };

  }



}
